import _, {uniq} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {Edge} from 'reactflow';

import {
  useLazyUnmountCancelToken,
  usePrevious,
} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {ExtendedError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {ChannelTypesEnum} from '../../../../../libs/shared/core/src/models/api/channelCommonTypes';
import {ChannelModel} from '../../../../../libs/shared/core/src/models/api/channelModel';
import {
  AiKnowledgeSourceTypesEnum,
  ChannelPreferenceFrontChatAiKnowledgeSourceModel,
} from '../../../../../libs/shared/core/src/models/api/channelPreferencesModel';
import {CompanyFeaturesEnum} from '../../../../../libs/shared/core/src/models/api/company/companyModel';
import {
  ApiDecisionTree,
  DecisionTreeModel,
  DecisionTreeTypesEnum,
} from '../../../../../libs/shared/core/src/models/api/decisionTrees/decisionTreeModel';
import {InboxModel} from '../../../../../libs/shared/core/src/models/api/inboxModel';
import {
  KnowledgeBaseModel,
  KnowledgeBaseStatusesEnum,
  SEMANTIC_SEARCH_BETA_LOCALES,
  SEMANTIC_SEARCH_SUPPORTED_LOCALES,
} from '../../../../../libs/shared/core/src/models/api/knowledgeBaseModel';
import {reportToBugsnag} from '../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {postApiAsync} from '../../../../../libs/shared/core/src/services/http/http';
import {
  HttpError,
  HttpNetworkError,
  HttpTimeoutError,
} from '../../../../../libs/shared/core/src/services/http/httpError';
import {getIsUserGlobalAdmin} from '../../../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {fetchChannelByIdAsyncAction} from '../../../../../libs/shared/core/src/store/data/channels/commonChannelsActions';
import {getFlatPublishedExternalKnowledgeBases} from '../../../../../libs/shared/core/src/store/data/knowledgeBases/knowledgeBasesSelectors';
import {getHasChatbotTransferConversation} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {TagModel} from '../../../../models/api/tagModel';
import {UseFetchAllChannelsSearchOptions} from '../../../../queries/channelQueries';
import {
  CreateDecisionTree,
  uploadChatBotAttachmentAsync,
} from '../../../../settings/chatbots/settingsChatBots/api';
import {CustomFields} from '../../../../settings/editors/commons/customFieldsModel';
import {useFetchAllChannels} from '../../../../settings/fetchers/channels';
import {useFetchSubtreesForDecisionTreeId} from '../../../../settings/fetchers/decisionTrees';
import {useTags} from '../../../../settings/rules/ruleEditor/parameters/tagParameter';
import {
  getCompany,
  getCompanyUrl,
  getFindHasCompanyFeature,
  getHasTrialFeatureAiAnswers,
} from '../../../../store/data/company/companySelectors';
import {fetchInboxByIdAsyncAction} from '../../../../store/data/inboxes/inboxesActions';
import {defaultChatBotBuilderSubtreeState} from '../defaultTree';
import {buildDefaultChatBotBuilderExpressions} from '../expressions/expressionHelpers';
import {gatherNodeTagIdsIncludeSubtrees} from '../helpers';
import {
  ChatBotAttachmentItem,
  ChatBotBuilderErrorReasonsEnum,
  ChatBotBuilderFeatureFlags,
  ChatBotBuilderNode,
} from '../types';
import {useDisabledNodeIds} from './useDisabledNodeIds';

/*
 * Types.
 */

export interface ChatBotBuilderAppProps {
  featureFlags: ChatBotBuilderFeatureFlags;
  isUserGlobalAdmin: boolean;
  hasQualBotsFeature: boolean;
  hasPremiumFeatures: boolean;
  isOnLegacyGrowthScalePlan: boolean;
  shouldRenderTrialBanner: boolean | undefined;
  companyCreatedAt: number;
  customFields: CustomFields;
  errorReason?: ChatBotBuilderErrorReasonsEnum;
  channel?: ChannelModel;
  channelList: ReturnType<typeof useFetchAllChannels> & {
    fetchAllChannelOptions: UseFetchAllChannelsSearchOptions;
    setFetchAllChannelOptions: React.Dispatch<React.SetStateAction<UseFetchAllChannelsSearchOptions>>;
    isChannelListActive: boolean;
    setIsChannelListActive: (isActive: boolean) => void;
  };
  inbox?: InboxModel;
  idTagMap: Record<number, TagModel>;
  isKnowledgeBaseEnabledForAi: boolean;
  aiKnowledgeSources: ReadonlyArray<ChannelPreferenceFrontChatAiKnowledgeSourceModel>;
  knowledgeBase: KnowledgeBaseModel | undefined;
  subtrees: ReadonlyArray<DecisionTreeModel>;
  subtreesStatus: AsyncStatusesEnum;
  refetchSubtrees: () => void;
  disabledNodeIds: ReadonlyArray<string>;
  onSubtreeCreate: (name: string) => Promise<DecisionTreeModel | undefined>;
  isSimpleEditor?: boolean;
  uploadFiles: (files: ReadonlyArray<File>) => Promise<Array<ChatBotAttachmentItem>>;
}

/*
 * Hooks.
 */

/**
 * ChatBotBuilder and its children should not directly select or dispatch to the redux store. This hook can be
 * used by ChatBotBuilder's parent component to retrieve values from the store which are needed for nodes such
 * as the Custom Field case. Note that this will also fetch the channel and inbox that is associated with the
 * provided channelId.
 */
export function useFetchChatBotBuilderAppProps(
  namespace: string,
  channelId: number | undefined,
  customFields: CustomFields,
  nodes: ReadonlyArray<ChatBotBuilderNode>,
  edges: ReadonlyArray<Edge<any>>,
  decisionTreeId?: number,
  hasPremiumFeatures?: boolean,
  errorReason?: ChatBotBuilderErrorReasonsEnum,
  isSimpleEditor?: boolean,
): ChatBotBuilderAppProps {
  // Feature flags.
  const featureFlags = useChatBotBuilderFeatureFlags();

  const [fetchAllChannelOptions, setFetchAllChannelOptions] = useState<UseFetchAllChannelsSearchOptions>({});
  const [isChannelListActive, setIsChannelListActive] = useState<boolean>(false);

  const [channel, setChannel] = useState<ChannelModel | undefined>();
  const [inbox, setInbox] = useState<InboxModel | undefined>();

  const shouldRenderTrialBanner = useAppSelector(getHasTrialFeatureAiAnswers);
  const isUserGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const company = useAppSelector(getCompany);
  const findHasCompanyFeature = useAppSelector(getFindHasCompanyFeature);
  const hasQualBots = findHasCompanyFeature(CompanyFeaturesEnum.CHAT_BOTS);

  const companyUrl = useAppSelector(getCompanyUrl);
  const buildUnmountCancelToken = useLazyUnmountCancelToken();

  const prevNamespace = usePrevious(channel?.namespace);
  const prevDecisionTreeId = usePrevious(decisionTreeId);

  const {
    subtrees,
    status: subtreesStatus,
    refetch: refetchSubtrees,
  } = useFetchSubtreesForDecisionTreeId(namespace, decisionTreeId);
  const {channels, loadMore, moreStatus} = useFetchAllChannels(fetchAllChannelOptions, {
    enabled: Boolean(isChannelListActive),
  });

  useEffect(() => {
    if (channel?.namespace !== prevNamespace || decisionTreeId !== prevDecisionTreeId) {
      refetchSubtrees();
    }
  }, [channel?.namespace, decisionTreeId, prevDecisionTreeId, prevNamespace, refetchSubtrees]);

  const {tagIds, maybeTagIds} = useMemo(
    () => gatherNodeTagIdsIncludeSubtrees(nodes, subtrees),
    [nodes, subtrees],
  );

  // Only re-compute the current `idTagMap` whenever a change in `allVisibleTags` is detected.
  // This is for stability, as `idTagMap` is eventually passed to the memoized context.
  const {allVisibleTags} = useTags(uniq([...tagIds, ...maybeTagIds]), '');
  const prevAllVisibleTags = usePrevious(allVisibleTags);

  const [idTagMap, setIdTagMap] = useState<Record<number, TagModel>>(
    allVisibleTags.reduce((currentTagIdMap, tag) => ({...currentTagIdMap, [tag.id]: tag}), {}),
  );
  useEffect(() => {
    if (_.isEqual(allVisibleTags, prevAllVisibleTags)) {
      return;
    }

    setIdTagMap(allVisibleTags.reduce((currentTagIdMap, tag) => ({...currentTagIdMap, [tag.id]: tag}), {}));
  }, [allVisibleTags, prevAllVisibleTags]);

  // The bot builder does not manage the knowledge base, but it is provided context on the channel's knowledge base.
  const knowledgeBases: ReadonlyArray<KnowledgeBaseModel> = useAppSelector(
    getFlatPublishedExternalKnowledgeBases,
  );
  const [isKnowledgeBaseEnabledForAi, channelKnowledgeBase, aiKnowledgeSources] = useMemo(() => {
    // Do not attempt to find bot builder's KB unless this is a chat channel that has enabled the KB preference.
    if (!channel || channel.type !== ChannelTypesEnum.FRONT_CHAT) {
      return [false, undefined];
    }

    const {aiKnowledgeSources: knowledgeSources} = channel.preferences;
    return [
      channel.preferences.isKnowledgeBaseEnabledForAi,
      knowledgeBases.find((knowledgeBase) => {
        // If the source is a KB and the current KB we are looking at is the correct one.
        if (
          knowledgeSources[0]?.type === AiKnowledgeSourceTypesEnum.KNOWLEDGE_BASE &&
          knowledgeBase.id === knowledgeSources[0].id
        ) {
          // Make sure the KB is a valid source.
          return [...SEMANTIC_SEARCH_SUPPORTED_LOCALES, ...SEMANTIC_SEARCH_BETA_LOCALES].some(
            (locale) => knowledgeBase.statuses[locale] === KnowledgeBaseStatusesEnum.PUBLISHED,
          );
        }
        return false;
      }),
      knowledgeSources,
    ];
  }, [channel, knowledgeBases]);

  const dispatch = useAppDispatch();

  const disabledNodeIds = useDisabledNodeIds(nodes, edges, channel, errorReason, subtrees);

  useEffect(() => {
    const fetchDecisionTreeChannel = async () => {
      const decisionTreeChannel = channelId
        ? await dispatch(fetchChannelByIdAsyncAction(channelId))
        : undefined;

      if (decisionTreeChannel && decisionTreeChannel.type === ChannelTypesEnum.FRONT_CHAT) {
        setChannel(decisionTreeChannel);

        const decisionTreeInbox = await dispatch(fetchInboxByIdAsyncAction(decisionTreeChannel?.inboxId));

        if (decisionTreeInbox) {
          setInbox(decisionTreeInbox);
        }
      }
    };

    try {
      fetchDecisionTreeChannel();
    } catch (error) {
      if (!(error instanceof HttpNetworkError || error instanceof HttpTimeoutError)) {
        reportToBugsnag(new ExtendedError('Failed to fetch channel', error));
      }
    }
  }, [channelId, channel, inbox, dispatch]);

  const onSubtreeCreate = useCallback(
    async (subtreeName: string) => {
      if (!decisionTreeId || !channel) {
        throw new Error('Failed to create subtree with missing parentDecisionTree or channel');
      }

      // TODO: Show error here if there will be a name collision.

      const defaultSubtreeData = {
        nodes: defaultChatBotBuilderSubtreeState.nodes,
        edges: defaultChatBotBuilderSubtreeState.edges,
      };

      const subtreeCreateData: CreateDecisionTree = {
        name: subtreeName,
        channel_ids: [],
        settings: {},
        namespace: channel.namespace,
        channel_type: channel.type,
        company_id: company.id,
        tree_type: DecisionTreeTypesEnum.SUBTREE,
        parent_decision_tree_id: decisionTreeId,
        tree_data: defaultSubtreeData,
        subtree_ids: [],
        expressions: buildDefaultChatBotBuilderExpressions(),
      };

      try {
        const newSubtree = await postApiAsync(
          ApiDecisionTree,
          `${companyUrl}/namespaces/${channel.namespace}/decision_trees`,
          subtreeCreateData,
          {
            cancelToken: buildUnmountCancelToken(),
          },
        );

        // TODO: Interface event for subtree create click

        // Fetch the subtrees again now that we've created a new one for the list.
        refetchSubtrees();
        return newSubtree;
      } catch (error) {
        if (!(error instanceof HttpError)) {
          reportToBugsnag(error);
        }
        throw error;
      }
    },
    [buildUnmountCancelToken, channel, company.id, companyUrl, decisionTreeId, refetchSubtrees],
  );

  const uploadFiles = async (files: ReadonlyArray<File>) => {
    const uploadedAttachments: Array<ChatBotAttachmentItem> = [];
    const uploadPromises = files.map(async (file) => {
      const fileAttachment = {
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        isErred: false,
      };
      try {
        const att = await uploadChatBotAttachmentAsync(
          companyUrl,
          namespace || '',
          decisionTreeId || 0,
          file,
        );

        uploadedAttachments.push({...att, fileName: file.name});
      } catch (err) {
        uploadedAttachments.push({...fileAttachment, isErred: true});
      }
    });
    await Promise.all(uploadPromises);
    return uploadedAttachments;
  };

  return {
    featureFlags,
    isUserGlobalAdmin,
    companyCreatedAt: company.createdAt,
    customFields,
    errorReason,
    hasPremiumFeatures: Boolean(hasPremiumFeatures),
    hasQualBotsFeature: hasQualBots,
    // If the company doesn't have access to premium features but does have access to Qualification Bots
    isOnLegacyGrowthScalePlan: Boolean(!hasPremiumFeatures && hasQualBots),
    shouldRenderTrialBanner: !isSimpleEditor && shouldRenderTrialBanner,
    channel,
    channelList: {
      channels,
      loadMore,
      moreStatus,
      fetchAllChannelOptions,
      setFetchAllChannelOptions,
      isChannelListActive,
      setIsChannelListActive,
    },
    inbox,
    idTagMap,
    isKnowledgeBaseEnabledForAi,
    aiKnowledgeSources: aiKnowledgeSources ?? [],
    knowledgeBase: channelKnowledgeBase,
    subtrees: subtrees ?? [],
    subtreesStatus,
    refetchSubtrees,
    disabledNodeIds,
    onSubtreeCreate,
    isSimpleEditor,
    uploadFiles,
  };
}

function useChatBotBuilderFeatureFlags(): ChatBotBuilderFeatureFlags {
  const hasChatbotTransferConversation = useAppSelector(getHasChatbotTransferConversation);

  return useMemo(
    () => ({
      hasChatbotTransferConversation,
    }),
    [hasChatbotTransferConversation],
  );
}
